import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { debounce } from "lodash";
import Header from "./components/headerFooter/Header";
import Footer from "./components/headerFooter/Footer";
import Userlogin from "./pages/Login";
import Userregister from "./pages/Register";
import Resetpwd from "./pages/Resetpwd";
import Property from "./pages/Property";
import PropertyListing from "./pages/PropertyListing";
import NewBuildListing from "./pages/NewBuildListing";
import NewBuildDetail from "./pages/NewBuildDetail";
import ReferralPage from './pages/Referral';
import VerifyOtp from "./pages/VerifyOtp";
import VerifyOtpPassword from "./pages/VerifyOtpPassword";
import Profile from "./pages/Profile";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import useThemeStore from "./store/themeStore";
import { getTheme, ThemeProps } from "./utils/theme";
import WhiteLabel from "./pages/WhiteLabel";
import Blog from "./pages/Blog";
import ScrollToTop from "./ScrollToTop";
import RoutePageViewTracker from "./components/RoutePageViewTracker";
import ServicesPage from "./pages/Services";
import PropertyValuation from "./pages/PropertyValuation";
import Maintenance from "./pages/Maintenance";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import Notifications from "./pages/Notifications";
import Feedback from "./pages/Feedback";
import OAuthCallback from "./pages/OAuthCallback";
import OAuthTermsRequired from "./pages/OAuthTermsRequired";
import SandboxProperty from "./pages/SandboxProperty";
import useTokenStore from "./store/store";
import "./App.css";
import { Helmet } from "react-helmet";
import { refreshSitemap } from './utils/sitemapGenerator';
import ClarityAnalytics from './components/ClarityAnalytics';
import CookieConsentManager from './components/CookieConsentManager';
import MPGWidget from "./pages/components/MPGWidget";
import PwaInstallModal from './components/PwaInstallModal';
import { applyPwaHeadTags } from './utils/pwaUtils';
import { logPwaEvent, logStandaloneLaunchOnMount } from './utils/pwaEventTracking';
import { PWA_INSTALLED } from './constants/pwaEvents';
import { useUserProfile } from './hooks/useUserProfile';
import { triggerRedirectIfRequired } from './utils/triggerRedirectIfRequired';

const REDIRECT_PAUSE_MS = 1000;

// SEO Component for Canonical URLs
const CanonicalUrl = ({ website }: { website?: string }) => {
  const location = useLocation();
  
  // Only generate canonical URLs for appropriate routes
  // Skip canonical for listing detail pages to prevent duplicate content
  const shouldHaveCanonical = !location.pathname.match(/^\/listing\/[^/]+/);
  
  if (!shouldHaveCanonical) {
    return null;
  }
  
  const canonicalUrl = website 
    ? `${website.replace(/\/$/, '')}${location.pathname}`
    : window.location.origin + location.pathname;
    
  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  );
};

// SEO Component for Common MetaTags
const CommonMetaTags = ({ theme }: { theme?: ThemeProps }) => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  
  // Don't add meta tags to non-public routes
  const isPrivatePage = ['/login', '/register', '/resetPassword', '/verifyPasswordotp', '/verifyotp'].includes(location.pathname);
  
  if (isPrivatePage || !theme) {
    return null;
  }
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <meta 
        name="description" 
        content={`${theme.name} offers professional property investment and portfolio building services. Find high-yielding buy-to-let properties sourced and vetted by ${theme.name}. Expert property sourcing and management solutions.`}
      />
      <meta 
        name="keywords" 
        content={`${theme.name}, property investment, buy-to-let properties, property portfolio, ${theme.website?.replace('https://', '').replace('www.', '')}, property sourcing, UK property investment`}
      />
      
      {/* Open Graph Tags */}
      <meta 
        property="og:title" 
        content={`${theme.name} | Build Your Property Investment Portfolio`}
      />
      <meta 
        property="og:description" 
        content={`Partner with ${theme.name} to discover premium property investment opportunities. Expert property sourcing and management services tailored to your investment goals.`}
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={theme.website || window.location.origin} />
      {theme.logo && <meta property="og:image" content={theme.logo} />}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${theme.name} | Property Investment Services`} />
      <meta 
        name="twitter:description" 
        content={`${theme.name} offers professional property investment services. Build your high-yielding property portfolio today.`}
      />
      {theme.logo && <meta name="twitter:image" content={theme.logo} />}
      
      {/* Structured Data - Only add on homepage */}
      {isHomePage && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": theme.name,
            "description": `${theme.name} provides professional property investment and portfolio building services in the UK.`,
            "url": theme.website || window.location.origin,
            "logo": theme.logo,
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "GB"
            },
            "sameAs": theme.socialLinks || []
          })}
        </script>
      )}
    </Helmet>
  );
};

// Message Types
enum MessageType {
  THEME_UPDATE = 'THEME_UPDATE',
  COLOR_UPDATE = 'COLOR_UPDATE',
  THEME_UPDATE_RECEIVED = 'THEME_UPDATE_RECEIVED',
  COLOR_UPDATE_RECEIVED = 'COLOR_UPDATE_RECEIVED',
  COLOR_UPDATE_ERROR = 'COLOR_UPDATE_ERROR'
}

// Constants
const DEBOUNCE_MS = 300;

// Demo site defaults
const DEMO_DEFAULTS = {
  color1: '#1e1b4b',
  color2: '#4f46e5',
  txtColor: '#000000',
  logo: 'https://demo.whitelabelproperty.com/logo.svg',
  favIcon: 'https://demo.whitelabelproperty.com/favicon.ico'
};

// Allowed origins configuration
const getPort = (): string => {
  if (typeof window !== 'undefined' && window.location.port) {
    return `:${window.location.port}`;
  }
  return ':3000'; // Default port for Create React App
};

const ALLOWED_ORIGINS = [
  'https://demo.whitelabelproperty.com',
  'https://www.demo.whitelabelproperty.com',
  'https://main.dfcttlwz2z1ar.amplifyapp.com',
  'https://www.main.dfcttlwz2z1ar.amplifyapp.com',
  'https://whitelabelproperty.com',
  'https://www.whitelabelproperty.com',
  'https://app.whitelabelproperty.com',
  'https://www.app.whitelabelproperty.com',
  `http://localhost${getPort()}`
].map(origin => origin.replace(/\/$/, ''));

// Helper functions
const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

const getThemeValue = (key: keyof typeof DEMO_DEFAULTS, value: any): any => {
  if (isEmpty(value) && key in DEMO_DEFAULTS) {
    return DEMO_DEFAULTS[key];
  }
  return value;
};

const normalizeOrigin = (origin: string): string => {
  return origin.replace(/\/$/, '').replace(/^(https?:\/\/)www\./, '$1');
};

const isAllowedOrigin = (origin: string): boolean => {
  const normalizedOrigin = normalizeOrigin(origin);
  return ALLOWED_ORIGINS.some(allowed => normalizeOrigin(allowed) === normalizedOrigin);
};

// Store parent origin when we receive messages
let parentOrigin: string | null = null;

// Get parent window origin dynamically
const getParentOrigin = (): string => {
  // If we've stored the parent origin from a message, use it
  if (parentOrigin) {
    return parentOrigin;
  }
  
  // Try to get from window.parent.location (will fail if cross-origin, which is expected)
  try {
    if (window.parent && window.parent !== window && window.parent.location) {
      const origin = window.parent.location.origin;
      if (isAllowedOrigin(origin)) {
        parentOrigin = origin;
        return origin;
      }
    }
  } catch (e) {
    // Cross-origin, can't access parent.location - this is expected
  }
  
  // Try to get from document.referrer
  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      const origin = referrerUrl.origin;
      if (isAllowedOrigin(origin)) {
        parentOrigin = origin;
        return origin;
      }
    } catch (e) {
      // Invalid referrer URL
    }
  }
  
  // Fallback: try to match any allowed origin that matches current window's pattern
  const currentOrigin = window.location.origin;
  const matchingOrigin = ALLOWED_ORIGINS.find(origin => {
    const normalized = normalizeOrigin(origin);
    const normalizedCurrent = normalizeOrigin(currentOrigin);
    // Check if they're related (e.g., www vs non-www)
    return normalized === normalizedCurrent || 
           normalized.replace('https://', 'https://www.') === normalizedCurrent ||
           normalizedCurrent.replace('https://', 'https://www.') === normalized;
  });
  
  if (matchingOrigin) {
    return matchingOrigin;
  }

  // Last resort: use current window origin so partner domains (e.g. trustbricks.co.uk) work
  return window.location.origin;
};

// Components
const ConditionalHeader = () => {
  const location = useLocation();
  const noHeaderRoutes = ['/maintenance'];
  const theme = useThemeStore((state) => state.theme) as ThemeProps;
  const { profile } = useUserProfile();

  // Routes where MPG widget should appear - add to this list as needed
  const mpgWidgetRoutes = ['/listing', '/Services'];

  // Check if current path matches MPG widget routes (including /listing/:id patterns)
  const showMpgWidget = theme?.hybrid || mpgWidgetRoutes.some(route =>
    location.pathname === route || location.pathname.startsWith('/listing/')
  );

  const widgetUserObject = profile
    ? {
        name: `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim(),
        email: profile.email ?? '',
        phoneNumber: profile.phone ?? '',
      }
    : { name: '', phoneNumber: '', email: '' };

  return noHeaderRoutes.includes(location.pathname) ? null : (
    <>
      <Header />
      {showMpgWidget && (
        <div className="fixed top-[72px] sm:top-[112px] left-0 right-0 z-[999] w-full">
          <MPGWidget
            apiKey={theme?.mpgWidgetApiKey || ''}
            type="ticker"
            userObject={widgetUserObject}
            themeColor1={theme?.color1 || null}
            themeColor2={theme?.color2 || null}
            themeTxtColor={theme?.txtColor || null}
            themeButtonTxtColor={theme?.buttonTxtColour || null}
            widgetId="mpg-widget-1"
            onInitialized={(_detail) => {}}
            onProductSelected={(_product) => {}}
            onError={(error) => {
              console.error('Widget error:', error);
            }}
          />
        </div>
      )}
    </>
  );
};

const ConditionalFooter = () => {
  const location = useLocation();
  const noFooterRoutes = ['/login', '/register', '/resetPassword', '/verifyPasswordotp', '/verifyotp', '/maintenance'];
  if (noFooterRoutes.includes(location.pathname)) return null;
  return <Footer />;
};

const PrivateRoute = ({ element }: { element: JSX.Element }) => {
  const location = useLocation();
  const getToken = useTokenStore((state) => state.token);
  
  if (!getToken) {
    const intendedUrl = location.pathname + location.search;
    return (
      <Navigate
        to={`/login?redirectTo=${encodeURIComponent(intendedUrl)}`}
        replace
      />
    );
  }
  return element;
};

function App() {
  const setTheme = useThemeStore((state) => state.setTheme);
  const currentTheme = useThemeStore((state) => state.theme);
  const [loading, setLoading] = useState(true);

  // Add this effect to update the title as soon as possible
  useEffect(() => {
    const updateTitle = () => {
      const savedTheme = localStorage.getItem('whitelabel_theme');
      if (savedTheme) {
        try {
          const parsedTheme = JSON.parse(savedTheme);
          if (parsedTheme.name) {
            document.title = `Property Investment Platform | ${parsedTheme.name}`;
          }
        } catch (err) {
          console.error('Error parsing saved theme:', err);
        }
      }
    };
    
    // Try to set title from cached theme immediately
    updateTitle();
  }, []);

  // PWA lifecycle telemetry: a standalone (installed-app) launch on mount — first
  // launch vs repeat — and the Android/Chrome `appinstalled` confirmation. The
  // ref guards against StrictMode's double effect invoke so one launch is logged
  // once. No site token → the helpers no-op.
  const pwaLaunchLoggedRef = useRef(false);
  useEffect(() => {
    if (!pwaLaunchLoggedRef.current) {
      pwaLaunchLoggedRef.current = true;
      logStandaloneLaunchOnMount();
    }
    const handleAppInstalled = () => logPwaEvent(PWA_INSTALLED);
    window.addEventListener("appinstalled", handleAppInstalled);
    return () => window.removeEventListener("appinstalled", handleAppInstalled);
  }, []);

  // Update LoadingScreen component to show the website name if available
  const LoadingScreen = () => {
    const [dots, setDots] = useState('.');
    const theme = useThemeStore.getState().theme;
    
    useEffect(() => {
      const interval = setInterval(() => {
        setDots(prev => prev.length >= 3 ? '.' : prev + '.');
      }, 500);
      return () => clearInterval(interval);
    }, []);

    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
        <Helmet>
          <title>
            {theme?.name 
              ? `Property Investment Platform | ${theme.name}` 
              : 'Property Investment Platform | Loading...'}
          </title>
        </Helmet>
        <div className="w-12 h-12 mb-4 flex items-center justify-center">
  <div 
    className="animate-spin rounded-full h-12 w-12 border-b-2"
    style={{ borderColor: theme?.color1 || DEMO_DEFAULTS.color1 }}
  />
</div>
        <div className="text-xl font-medium text-gray-700">
          {theme?.name 
            ? `Loading ${theme.name}${dots}`
            : `Loading your property investment platform${dots}`}
        </div>
        <div className="mt-2 text-sm text-gray-500">
          Please wait while we personalise your experience
        </div>
      </div>
    );
  };

  // Load theme from localStorage
  const loadSavedTheme = useCallback(() => {
    try {
      const savedTheme = localStorage.getItem('whitelabel_theme');
      if (savedTheme) {
        const parsedTheme = JSON.parse(savedTheme);
        const themeWithDefaults = {
          ...parsedTheme,
          color1: getThemeValue('color1', parsedTheme.color1),
          color2: getThemeValue('color2', parsedTheme.color2),
          txtColor: getThemeValue('txtColor', parsedTheme.txtColor),
          logo: getThemeValue('logo', parsedTheme.logo),
          favIcon: getThemeValue('favIcon', parsedTheme.favIcon),
          buttonTxtColour: parsedTheme.buttonTxtColour,
          buttonHoverTxtColour: parsedTheme.buttonHoverTxtColour
        };
        setTheme(themeWithDefaults);
        setLoading(false);
        console.debug('Loaded saved theme');
        return true;
      }
    } catch (err) {
      console.error('Error loading saved theme:', err);
    }
    return false;
  }, [setTheme]);

  // Fetch theme if not in localStorage
  const fetchTheme = useCallback(async () => {
    // Load from localStorage as a temporary state while fetching
    loadSavedTheme();
    
    try {
      // Always fetch from server
      const response = await getTheme();
      if (response) {
        const themeWithDefaults = {
          ...response,
          color1: getThemeValue('color1', response.color1),
          color2: getThemeValue('color2', response.color2),
          txtColor: getThemeValue('txtColor', response.txtColor),
          logo: getThemeValue('logo', response.logo),
          favIcon: getThemeValue('favIcon', response.favIcon),
          buttonTxtColour: response.buttonTxtColour,
          buttonHoverTxtColour: response.buttonHoverTxtColour
        };
        setTheme(themeWithDefaults);
        localStorage.setItem('whitelabel_theme', JSON.stringify(themeWithDefaults));
        applyPwaHeadTags(themeWithDefaults);
      }
    } catch (err) {
      console.error('Error fetching theme:', err);
      // We already loaded from localStorage as fallback
    } finally {
      setLoading(false);
    }
  }, [loadSavedTheme, setTheme]);
  

  // Debounced color update sender
  // eslint-disable-next-line
  const debouncedSendColorUpdate = useCallback(
    debounce((colors: any) => {
      try {
        window.parent.postMessage({
          type: MessageType.COLOR_UPDATE,
          colors
        }, getParentOrigin());
        console.debug('Color update sent');
      } catch (err) {
        console.error('Failed to send color update:', err);
      }
    }, DEBOUNCE_MS),
    []
  );

  // Handle incoming theme updates
  const handleMessage = useCallback((event: MessageEvent) => {
    if (!isAllowedOrigin(event.origin)) return;

    // Store the parent origin for future postMessage calls
    parentOrigin = event.origin;

    const { type, theme } = event.data;
    if (type === MessageType.THEME_UPDATE && theme) {
      try {
        const themeWithDefaults = {
          ...theme,
          color1: getThemeValue('color1', theme.color1),
          color2: getThemeValue('color2', theme.color2),
          txtColor: getThemeValue('txtColor', theme.txtColor),
          logo: getThemeValue('logo', theme.logo),
          favIcon: getThemeValue('favIcon', theme.favIcon)
        };
        
        setTheme(themeWithDefaults);
        localStorage.setItem('whitelabel_theme', JSON.stringify(themeWithDefaults));
        
        // Silent acknowledgment - use stored parent origin or event origin
        window.parent.postMessage({
          type: MessageType.THEME_UPDATE_RECEIVED,
          status: 'success'
        }, getParentOrigin());
      } catch (err) {
        console.error('Error processing theme update:', err);
      }
    }
  }, [setTheme]);

  // Initialize theme fetch
  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);
  
  // Initialize message listener
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Watch for theme changes and send updates
  useEffect(() => {
    if (currentTheme?.color1 || currentTheme?.color2 || currentTheme?.txtColor) {
      const colors = {
        color1: currentTheme.color1 || DEMO_DEFAULTS.color1,
        color2: currentTheme.color2 || DEMO_DEFAULTS.color2,
        txtColor: currentTheme.txtColor || DEMO_DEFAULTS.txtColor
      };
      
      debouncedSendColorUpdate(colors);
      localStorage.setItem('whitelabel_theme', JSON.stringify({
        ...currentTheme,
        ...colors
        // eslint-disable-next-line
      }));
    }
    // eslint-disable-next-line
  }, [currentTheme?.color1, currentTheme?.color2, currentTheme?.txtColor, debouncedSendColorUpdate]);

  // Generate and update sitemap when theme changes
  useEffect(() => {
    if (currentTheme?.website) {
      // Generate sitemap content
      refreshSitemap(currentTheme);
      
      // Update robots.txt sitemap URL
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (!robotsMeta) {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = `index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1`;
        document.head.appendChild(meta);
      }
    }
    // eslint-disable-next-line 
  }, [currentTheme?.website]);

  // Hostname-based redirect (Option B): schedule once per theme change, not per render
  useEffect(() => {
    if (currentTheme?.onHold || !currentTheme) {
      return;
    }

    const timerId = setTimeout(
      () =>
        triggerRedirectIfRequired({
          redirectToUrl: currentTheme.redirectToUrl,
          website: currentTheme.website,
        }),
      REDIRECT_PAUSE_MS
    );

    return () => clearTimeout(timerId);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reschedule only when redirect inputs change
  }, [
    currentTheme?.onHold,
    currentTheme?.redirectToUrl,
    currentTheme?.website,
  ]);

  if (currentTheme?.onHold) {
    return <Maintenance />;
  }

  if (loading) {
    return <LoadingScreen />;
  }
  
  return (
    <div className="App">
      <Router basename={"/"}>
        <RoutePageViewTracker />
        <ScrollToTop>
          <Fragment>
            {/* Global SEO Elements */}
            <Helmet>
              <title>
                {currentTheme?.name 
                  ? `Property Investment Platform | ${currentTheme.name}`
                  : 'Property Investment Platform'}
              </title>
              
              {/* Favicon */}
              {currentTheme?.favIcon && (
                <link rel="icon" href={currentTheme.favIcon} />
              )}
              
              {/* Sitemap link */}
              {currentTheme?.website && (
                <link 
                  rel="sitemap"
                  type="application/xml"
                  href={`${currentTheme.website}/sitemap.xml`}
                />
              )}
              
              {/* Mobile viewport */}
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=contain"
              />
              
              {/* Theme color */}
              <meta name="theme-color" content={currentTheme?.color1 || "#000000"} />
            </Helmet>
            
            {/* Add canonical URL */}
            <CanonicalUrl website={currentTheme?.website} />
            
            {/* Add common meta tags */}
            <CommonMetaTags theme={currentTheme} />
            
            {/* Main app content */}
            <ConditionalHeader />
            <CookieConsentManager />
            <ClarityAnalytics theme={currentTheme} />
            <Routes>
              <Route
                path="/"
                element={
                  currentTheme && (currentTheme as ThemeProps).hybrid ? (
                    <PropertyListing />
                  ) : (
                    <WhiteLabel />
                  )
                }
              />
              {/* Public Routes */}
              <Route path="/login" element={<Userlogin />} />
              <Route path="/register" element={<Userregister />} />
              <Route path="/auth/callback" element={<OAuthCallback />} />
              <Route path="/auth/terms-required" element={<OAuthTermsRequired />} />
              <Route path="/resetPassword" element={<Resetpwd />} />
              <Route path="/verifyPasswordOtp" element={<VerifyOtpPassword />} />
              <Route path="/verifyotp" element={<VerifyOtp />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/propertyvaluation" element={<PropertyValuation />} />
              <Route path="/listing" element={<PropertyListing />} />
              <Route path="/new-builds" element={<NewBuildListing />} />
              <Route path="/Referral" element={<ReferralPage />} />
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/sandbox/:token" element={<SandboxProperty />} />
              {/* Private Routes */}
              <Route path="/dashboard" element={<PrivateRoute element={<Dashboard />} />} />
              <Route path="/profile" element={<PrivateRoute element={<Profile />} />} />
              <Route path="/settings" element={<PrivateRoute element={<Settings />} />} />
              <Route path="/notifications" element={<PrivateRoute element={<Notifications />} />} />
              <Route path="/feedback" element={<PrivateRoute element={<Feedback />} />} />
              <Route path="/listing/:id" element={<PrivateRoute element={<Property />} />} />
              <Route path="/new-build/:id" element={<PrivateRoute element={<NewBuildDetail />} />} />
              <Route path="/listing/:id/failure" element={<PrivateRoute element={<Property />} />} />
              <Route path="/listing/:id/success" element={<PrivateRoute element={<Property />} />} />
            </Routes>
            <ConditionalFooter />

            {/* PWA install — centralised modal (auto-opens on eligible mobile landings) */}
            <PwaInstallModal />
          </Fragment>
        </ScrollToTop>
      </Router>
    </div>
  );
}

export default App;